// ChecklistFill — Combined Invullen + Detail view.
import { getAccessToken } from '@/lib/tokenStore';
// Uses `readOnly` prop to switch between editable form and read-only detail panel.
// Detail mode additionally renders: score summary, PDF export, AI analysis.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

// E14a/F17: één melding voor alle paden (autosave, opslaan, indienen)
const GEMIST_MELDING = 'Deze checklist is gesloten als gemist en kan niet meer worden aangepast.';
import {
  Loader2, ArrowLeft, AlertTriangle, CheckCircle2, Send,
  Save, Calendar, Trash2, Download, User, Sparkles, ClipboardList,
  BarChart3,
} from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

import {
  API_V2, MAAND_NAMEN, hdrs, fmtDate, fmtDateShort,
  scoreColor, statusBadge, isMeterstandTemplate, bepaalVerbruikMaand,
} from './checklistUtils';
import { FotoUploadV2, FotoGrid, ItemRenderer } from './ChecklistFormElements';
import { SnapshotBadge } from './SnapshotBadge';
import { useAuth } from '../../contexts/AuthContext';

// v18.2.2 — Formatteert de 'laatst opgeslagen'-indicator. Vóór de eerste save (d=null)
// een statische hint zodat de auto-save discoverable is.
const formatLastSaved = (d) => {
  if (!d) return 'Wijzigingen worden elke 5 minuten automatisch opgeslagen.';
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return 'Laatst opgeslagen: zojuist';
  if (diffMin < 60) return `Laatst opgeslagen: ${diffMin} min geleden`;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `Laatst opgeslagen om ${hh}:${mm}`;
};

export const ChecklistFill = ({ checklistId, onBack, readOnly = false, onDelete }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [sectieAntwoorden, setSectieAntwoorden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [aiAnalyse, setAiAnalyse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [uitgebreid, setUitgebreid] = useState(null);
  const [uitgebreidLoading, setUitgebreidLoading] = useState(false);
  const startedRef = useRef(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [, forceTick] = useState(0);  // 30s re-render om het 'laatst opgeslagen'-label te verversen

  useEffect(() => {
    if (!checklistId) return;
    setLoading(true);
    fetch(`${API_V2}/checklists/${checklistId}`, { headers: hdrs() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setData(d);
          setItems(d.items || []);
          // Ensure sectie_antwoorden has entries for ALL template sections
          const tmplSecties = d.template?.secties || [];
          const existingSA = d.sectie_antwoorden || [];
          const existingIds = new Set(existingSA.map(sa => sa.sectie_id));
          const merged = [...existingSA];
          for (const sec of tmplSecties) {
            const secId = sec.id || sec._id;
            if (secId && !existingIds.has(secId)) {
              merged.push({ sectie_id: secId, toelichting: '', foto_urls: [], actiepunten: '' });
            }
          }
          setSectieAntwoorden(merged);
        }
      })
      .catch(() => toast.error('Laden mislukt'))
      .finally(() => setLoading(false));
  }, [checklistId]);

  // Auto-start time registration
  useEffect(() => {
    if (!data || readOnly || startedRef.current) return;
    if (data.status === 'ingevuld' || data.status === 'ingevuld_te_laat') return;
    startedRef.current = true;
    fetch(`${API_V2}/checklists/${checklistId}/start`, { method: 'POST', headers: hdrs() }).catch(() => {});
  }, [data, readOnly, checklistId]);

  const updateItem = (itemId, updated) => {
    setItems(prev => prev.map(it => it.item_id === itemId ? updated : it));
  };

  const updateSectieAntwoord = useCallback((sectieId, field, val) => {
    setSectieAntwoorden(prev => {
      const exists = prev.some(sa => sa.sectie_id === sectieId);
      if (exists) {
        return prev.map(sa =>
          sa.sectie_id === sectieId ? { ...sa, [field]: val } : sa
        );
      }
      return [...prev, { sectie_id: sectieId, toelichting: '', foto_urls: [], actiepunten: '', [field]: val }];
    });
  }, []);

  const handleSaveDraft = async ({ silent = false } = {}) => {
    if (!silent) setSaving(true);
    try {
      const res = await fetch(`${API_V2}/checklists/${checklistId}`, {
        method: 'PUT', headers: hdrs(),
        body: JSON.stringify({ items, sectie_antwoorden: sectieAntwoorden }),
      });
      if (res.ok) setLastSaved(new Date());
      else if (res.status === 409) {
        // F17: dezelfde afhandeling als de autosave
        const err = await res.json().catch(() => ({}));
        if ((err.detail || '').toLowerCase().includes('gemist')) {
          setGeslotenGemist(true);
          toast.error(GEMIST_MELDING, { duration: 10000 });
          return;
        }
      }
      if (!silent) {
        if (res.ok) toast.success('Concept opgeslagen');
        else if (!geslotenGemist) toast.error('Opslaan mislukt', { duration: 8000 });
      }
    } catch { if (!silent) toast.error('Opslaan mislukt', { duration: 8000 }); }
    finally { if (!silent) setSaving(false); }
  };

  // v18.1.7 — Data-loss prevention: auto-save concept elke 5 minuten tijdens
  // invullen. Gebruikt useRef ipv state-closure omdat setInterval een stale
  // callback zou capturen (items/sectieAntwoorden zijn state, niet refs).
  // Dankzij de fetch-wrapper auto-refresh werkt de PUT ook na token-expiry.
  const draftStateRef = useRef({ items, sectieAntwoorden });
  // E14a/6.26e: een als gemist gesloten checklist is definitief dicht.
  const [geslotenGemist, setGeslotenGemist] = useState(false);

  useEffect(() => {
    draftStateRef.current = { items, sectieAntwoorden };
  }, [items, sectieAntwoorden]);

  useEffect(() => {
    if (readOnly || loading || !data || geslotenGemist) return;
    // Skip voor checklists die al ingediend zijn
    if (data.status === 'ingevuld' || data.status === 'ingevuld_te_laat') return;
    if (data.status === 'gemist') return;
    const interval = setInterval(async () => {
      const snap = draftStateRef.current;
      try {
        const res = await fetch(`${API_V2}/checklists/${checklistId}`, {
          method: 'PUT', headers: hdrs(),
          body: JSON.stringify({ items: snap.items, sectie_antwoorden: snap.sectieAntwoorden }),
        });
        if (res.ok) setLastSaved(new Date());  // hook op bestaand succespunt — mechaniek ongemoeid
        else if (res.status === 409) {
          setGeslotenGemist(true);
          toast.error(GEMIST_MELDING);
        }
      } catch { /* silent — auto-save mag niet storen */ }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [readOnly, loading, data, checklistId, geslotenGemist]);

  // R7.1: een gewijzigde fotoreeks (upload of verwijderen) meteen stil bewaren,
  // zodat 20 foto's niet verloren gaan als iemand binnen 5 minuten wegklikt.
  const fotoSig = JSON.stringify([items.map(i => i.foto_urls || []), sectieAntwoorden.map(s => s.foto_urls || [])]);
  const vorigeFotoSig = useRef(null);
  useEffect(() => {
    if (readOnly || loading || !data || geslotenGemist) return;
    if (['ingevuld', 'ingevuld_te_laat', 'gemist'].includes(data.status)) return;
    if (vorigeFotoSig.current === null || vorigeFotoSig.current === fotoSig) {
      vorigeFotoSig.current = fotoSig;
      return;
    }
    vorigeFotoSig.current = fotoSig;
    const snap = draftStateRef.current;
    fetch(`${API_V2}/checklists/${checklistId}`, {
      method: 'PUT', headers: hdrs(),
      body: JSON.stringify({ items: snap.items, sectie_antwoorden: snap.sectieAntwoorden }),
    }).then(res => {
      if (res.ok) setLastSaved(new Date());
      else if (res.status === 409) { setGeslotenGemist(true); toast.error(GEMIST_MELDING); }
    }).catch(() => { /* stil — de 5-minuten-autosave en 'Concept opslaan' blijven bestaan */ });
  }, [fotoSig, readOnly, loading, data, checklistId, geslotenGemist]);

  // v18.2.2 — Tik elke 30s zodat het relatieve 'laatst opgeslagen'-label
  // ('X min geleden') meeloopt. Triggert enkel een re-render; clearInterval bij unmount.
  useEffect(() => {
    if (readOnly) return;
    const t = setInterval(() => forceTick(n => n + 1), 30 * 1000);
    return () => clearInterval(t);
  }, [readOnly]);

  const handleSubmit = async () => {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.verplicht) continue;
      // foto-items bewaren hun waarde in foto_urls (niet in waarde) — valideer
      // op dezelfde key als de backend (_is_item_filled), anders wordt een
      // ingevuld foto-item ten onrechte als leeg gemarkeerd.
      const empty = item.type === 'foto'
        ? !(item.foto_urls && item.foto_urls.length > 0)
        : (item.waarde === null || item.waarde === undefined ||
          (typeof item.waarde === 'string' && item.waarde.trim() === ''));
      if (empty) {
        toast.error(`Verplicht item niet ingevuld: ${item.naam}`);
        const el = document.querySelector(`[data-testid="item-${item.item_id}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_V2}/checklists/${checklistId}/indienen`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ items, sectie_antwoorden: sectieAntwoorden }),
      });
      if (res.ok) {
        const result = await res.json();
        setSubmitResult(result);
        toast.success('Checklist ingediend!');
      } else {
        const err = await res.json().catch(() => ({}));
        if (res.status === 409 && (err.detail || '').toLowerCase().includes('gemist')) {
          setGeslotenGemist(true);              // F17
          window.scrollTo({ top: 0, behavior: 'smooth' });
          toast.error(GEMIST_MELDING, { duration: 10000 });
          return;
        }
        // v18.1.7 — Scroll naar top + langere toast zodat user op lange
        // formulieren de foutmelding ook daadwerkelijk ziet. Voorheen
        // verscheen een 4-sec toast bovenaan terwijl user onderaan stond.
        window.scrollTo({ top: 0, behavior: 'smooth' });
        toast.error(err.detail || 'Indienen mislukt — probeer opnieuw', { duration: 10000 });
      }
    } catch {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.error('Indienen mislukt — probeer opnieuw', { duration: 10000 });
    }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;
  if (!data) return <div className="text-center py-12 text-slate-400">Checklist niet gevonden</div>;

  const tmpl = data.template || {};
  const schaal = tmpl.beoordeling_schaal || 10;
  const isVerlopen = data.status === 'verlopen';
  const isCompleted = data.status === 'ingevuld' || data.status === 'ingevuld_te_laat';
  const isGemist = data.status === 'gemist' || geslotenGemist;
  const effectiveReadOnly = readOnly || isCompleted || isGemist;

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const t = getAccessToken();
      const res = await fetch(`${API_V2}/checklists/${checklistId}/export-pdf`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const disp = res.headers.get('content-disposition');
        const fn = disp?.match(/filename="(.+)"/)?.[1] || 'checklist.pdf';
        a.href = url; a.download = fn; a.click();
        URL.revokeObjectURL(url);
        toast.success('PDF gedownload');
      } else toast.error('PDF export mislukt');
    } catch { toast.error('PDF export mislukt'); }
    finally { setExporting(false); }
  };

  const handleAiAnalyse = async () => {
    setAiLoading(true);
    try {
      const res = await fetch(`${API_V2}/checklists/${checklistId}/ai-analyse`, { method: 'POST', headers: hdrs() });
      if (res.ok) { const d = await res.json(); setAiAnalyse(d); }
      else toast.error('AI analyse mislukt');
    } catch { toast.error('AI analyse mislukt'); }
    finally { setAiLoading(false); }
  };

  const fetchUitgebreid = async (force = false) => {
    setUitgebreidLoading(true);
    try {
      const url = `${API_V2}/checklists/${checklistId}/uitgebreide-analyse${force ? '?force=true' : ''}`;
      const res = await fetch(url, { method: 'POST', headers: hdrs() });
      if (res.ok) {
        const d = await res.json();
        setUitgebreid(d);
      } else {
        toast.error('Uitgebreide analyse mislukt');
      }
    } catch {
      toast.error('Uitgebreide analyse mislukt');
    } finally {
      setUitgebreidLoading(false);
    }
  };

  // Determine sectie_velden_tonen: check checklist-level first, then template
  const sectieVeldenTonen = data.sectie_velden_tonen !== undefined
    ? data.sectie_velden_tonen
    : (data.template?.sectie_velden_tonen !== undefined
        ? data.template.sectie_velden_tonen
        : data.template_type !== 'persoonlijk');

  // Group items by section
  const secties = tmpl.secties || [];
  const itemsBySectie = {};
  items.forEach((it, idx) => {
    const key = it.sectie_id || '__none__';
    if (!itemsBySectie[key]) itemsBySectie[key] = [];
    itemsBySectie[key].push({ ...it, _idx: idx });
  });

  if (submitResult) {
    const isMeterstand = isMeterstandTemplate(data.template_naam);
    const doelMaand = isMeterstand ? bepaalVerbruikMaand() : null;
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4 max-w-md mx-auto px-4" data-testid="submit-success">
        <CheckCircle2 className="w-16 h-16 text-green-500" />
        <h2 className="text-2xl font-bold text-[#1f2548]">Checklist ingediend!</h2>
        {submitResult.score && (
          <div className="text-center">
            <span className={`text-4xl font-bold ${scoreColor(submitResult.score.percentage)}`}>
              {submitResult.score.percentage}%
            </span>
            <p className="text-sm text-slate-500 mt-1">
              {submitResult.score.goed_items}/{submitResult.score.totaal_checkbox_items} items goed
            </p>
          </div>
        )}
        {isMeterstand && submitResult.meterstand_verwerkt && doelMaand && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center" data-testid="meterstand-verwerkt-melding">
            <p className="text-sm text-blue-800 font-medium">Meterstanden opgeslagen en verwerkt als verbruik voor {MAAND_NAMEN[doelMaand.maand]} {doelMaand.jaar}.</p>
          </div>
        )}
        {submitResult.te_laat && (
          <Badge className="bg-amber-100 text-amber-700 text-sm px-3 py-1">
            <AlertTriangle className="w-4 h-4 mr-1" /> Ingevuld te laat
          </Badge>
        )}
        <Button onClick={onBack} className="mt-4 bg-[#1f2548] hover:bg-[#2a3060]" data-testid="back-to-overview-btn">
          Terug naar overzicht
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl mx-auto px-4 sm:px-0 pb-24" data-testid="checklist-fill">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg mt-0.5" data-testid="fill-back-btn">
          <ArrowLeft className="w-5 h-5 text-slate-500" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-[#1f2548]">{data.template_naam}</h1>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <span className="text-sm text-slate-500">{data.locatie_naam}</span>
            {data.invulling_modus === 'persoon' && (data.toegewezen_aan_naam || data.ingevuld_door_naam) && (
              <span className="text-sm text-slate-500 flex items-center gap-1">
                <User className="w-3 h-3" /> Voor: {data.toegewezen_aan_naam || data.ingevuld_door_naam}
              </span>
            )}
            {data.periode?.deadline && (
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Deadline: {fmtDateShort(data.periode.deadline)}
              </span>
            )}
            <Badge className={statusBadge(data.status, data.te_laat).cls}>
              {statusBadge(data.status, data.te_laat).label}
            </Badge>
          </div>
        </div>
        {/* Action buttons in header */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {isCompleted && (
            <Button variant="outline" size="sm" onClick={handleExportPdf} disabled={exporting} data-testid="export-pdf-btn">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            </Button>
          )}
          {onDelete && (
            <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 hover:bg-red-50"
              onClick={() => onDelete(data)} data-testid="detail-delete-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Summary card for completed checklists */}
      {isCompleted && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-x-6 gap-y-2" data-testid="completed-summary">
          {data.score && (
            <div>
              <p className="text-[10px] uppercase text-slate-400 font-medium">Score</p>
              <span className={`text-xl font-bold ${scoreColor(data.score.percentage)}`}>{data.score.percentage}%</span>
              <span className="text-xs text-slate-400 ml-1">{data.score.goed_items}/{data.score.totaal_checkbox_items}</span>
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase text-slate-400 font-medium">Ingevuld door</p>
            <p className="text-sm font-medium text-slate-700">{data.ingevuld_door_naam || '-'}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-slate-400 font-medium">Datum</p>
            <p className="text-sm text-slate-700">{fmtDate(data.ingevuld_op)}</p>
          </div>
          {data.tijdregistratie?.duur_seconden && (
            <div>
              <p className="text-[10px] uppercase text-slate-400 font-medium">Invultijd</p>
              <p className="text-sm text-slate-700">{Math.round(data.tijdregistratie.duur_seconden / 60)} min</p>
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase text-slate-400 font-medium">Status</p>
            <Badge className={data.te_laat ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
              {data.te_laat ? 'Te laat' : 'Op tijd'}
            </Badge>
          </div>
        </div>
      )}

      {isGemist && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3"
          data-testid="gemist-melding">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">
            Deze checklist is gesloten als gemist en kan niet meer worden aangepast.
          </p>
        </div>
      )}

      {/* Verlopen warning */}
      {isVerlopen && !effectiveReadOnly && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3" data-testid="verlopen-warning">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800 text-sm">Deze checklist is verlopen</p>
            <p className="text-xs text-red-600">Je kunt hem alsnog invullen maar hij wordt als 'te laat' gemarkeerd.</p>
          </div>
        </div>
      )}

      {/* Meterstand info banner */}
      {isMeterstandTemplate(data.template_naam) && !effectiveReadOnly && (() => {
        const doel = bepaalVerbruikMaand();
        return (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3" data-testid="meterstand-info-banner">
            <ClipboardList className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-800 text-sm">Meterstanden worden automatisch verwerkt</p>
              <p className="text-xs text-blue-600">
                De ingevulde meterstanden worden opgeslagen als verbruiksregistratie voor <strong>{MAAND_NAMEN[doel.maand]} {doel.jaar}</strong>.
                Vul de meterstand in op de laatste dag van de maand.
              </p>
            </div>
          </div>
        );
      })()}

      {/* Template-level info blocks */}
      {tmpl.info_blokken?.map((ib, i) => (
        <div key={i} className="bg-slate-100 border border-slate-200 rounded-xl p-4" data-testid={`info-blok-${i}`}>
          <p className="font-semibold text-sm text-slate-600 mb-1">{ib.titel}</p>
          <p className="text-sm text-slate-500 whitespace-pre-wrap">{ib.tekst}</p>
        </div>
      ))}

      {/* Snapshot badge (alleen op ingediende invullingen met bevroren template-snapshot) */}
      <SnapshotBadge
        template={tmpl}
        snapshotDate={data.template_snapshot?.snapshot_genomen_op}
        fallbackDate={data.ingevuld_op}
      />

      {/* Sections */}
      {secties.map((sectie) => {
        const sItems = itemsBySectie[sectie.id || sectie._id] || [];
        const sa = sectieAntwoorden.find(s => s.sectie_id === (sectie.id || sectie._id));
        return (
          <div key={sectie.id || sectie._id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`sectie-${sectie.naam}`}>
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
              <h2 className="font-semibold text-[#1f2548] text-sm sm:text-base">{sectie.naam}</h2>
            </div>
            {/* Section-level info blocks */}
            {sectie.info_blokken?.map((ib, ibi) => (
              <div key={ibi} className="mx-4 mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="font-semibold text-xs text-amber-700">{ib.titel}</p>
                <p className="text-xs text-amber-600 whitespace-pre-wrap">{ib.tekst}</p>
              </div>
            ))}
            <div className="divide-y divide-slate-50 px-4">
              {sItems.map((item) => (
                <ItemRenderer key={item.item_id} item={item} schaal={schaal}
                  onUpdate={(upd) => updateItem(item.item_id, upd)} readOnly={effectiveReadOnly} />
              ))}
            </div>
            {/* Section fields — conditional on sectie_velden_tonen or per-section flags */}
            {(sectieVeldenTonen || sectie.heeft_fotos !== false || sectie.heeft_toelichting !== false) && (
            <div className="px-4 py-3 bg-slate-50/50 border-t border-slate-100 space-y-3">
              {(sectieVeldenTonen || sectie.heeft_toelichting !== false) && sectie.heeft_toelichting !== false && (
              <div>
                <Label className="text-xs text-slate-500">Toelichting</Label>
                {effectiveReadOnly
                  ? <p className="text-sm text-slate-600 mt-1">{sa?.toelichting || '-'}</p>
                  : <Textarea value={sa?.toelichting || ''} onChange={e => updateSectieAntwoord(sectie.id || sectie._id, 'toelichting', e.target.value)}
                      placeholder="Toelichting bij deze sectie..." rows={2} className="text-sm" data-testid={`toelichting-${sectie.naam}`} />
                }
              </div>
              )}
              {sectie.heeft_fotos !== false && (
              <div>
                <Label className="text-xs text-slate-500">Foto's</Label>
                {effectiveReadOnly
                  ? (sa?.foto_urls?.length > 0 ? (
                      <div className="flex gap-2 flex-wrap mt-1">
                        <FotoGrid urls={sa.foto_urls} />
                      </div>
                    ) : <p className="text-sm text-slate-400 mt-1">Geen foto's</p>)
                  : <FotoUploadV2 urls={sa?.foto_urls || []} onChange={urls => updateSectieAntwoord(sectie.id || sectie._id, 'foto_urls', urls)} />
                }
              </div>
              )}
              {sectieVeldenTonen && (
              <div>
                <Label className="text-xs text-slate-500">Actiepunten</Label>
                {effectiveReadOnly
                  ? <p className="text-sm text-slate-600 mt-1">{sa?.actiepunten || '-'}</p>
                  : <Textarea value={sa?.actiepunten || ''} onChange={e => updateSectieAntwoord(sectie.id || sectie._id, 'actiepunten', e.target.value)}
                      placeholder="Actiepunten..." rows={2} className="text-sm" data-testid={`actiepunten-${sectie.naam}`} />
                }
              </div>
              )}
            </div>
            )}
          </div>
        );
      })}

      {/* Items without section */}
      {(itemsBySectie['__none__'] || []).length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="items-zonder-sectie">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
            <h2 className="font-semibold text-[#1f2548] text-sm">Overige items</h2>
          </div>
          <div className="divide-y divide-slate-50 px-4">
            {(itemsBySectie['__none__'] || []).map((item) => (
              <ItemRenderer key={item.item_id} item={item} schaal={schaal}
                onUpdate={(upd) => updateItem(item.item_id, upd)} readOnly={effectiveReadOnly} />
            ))}
          </div>
        </div>
      )}

      {/* Score display for completed checklists */}
      {isCompleted && data.score && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 text-center" data-testid="completed-score">
          <p className="text-sm text-slate-500 mb-1">Eindscore</p>
          <span className={`text-4xl font-bold ${scoreColor(data.score.percentage)}`}>{data.score.percentage}%</span>
          <p className="text-xs text-slate-400 mt-1">{data.score.goed_items}/{data.score.totaal_checkbox_items} items goed</p>
          {data.te_laat && <Badge className="bg-amber-100 text-amber-700 mt-2"><AlertTriangle className="w-3 h-3 mr-1" /> Te laat ingevuld</Badge>}
        </div>
      )}

      {/* AI Analyse section */}
      {isCompleted && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="ai-analyse-section">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <h3 className="font-semibold text-[#1f2548] text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> AI Analyse
            </h3>
            {!aiAnalyse && !data.ai_analyse?.tekst && (
              <Button size="sm" variant="outline" onClick={handleAiAnalyse} disabled={aiLoading} data-testid="generate-ai-btn">
                {aiLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                Genereer analyse
              </Button>
            )}
          </div>
          <div className="p-4">
            {aiLoading ? (
              <div className="flex items-center gap-2 text-slate-400 py-4 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Analyse genereren...
              </div>
            ) : (aiAnalyse?.tekst || data.ai_analyse?.tekst) ? (
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{aiAnalyse?.tekst || data.ai_analyse?.tekst}</p>
            ) : (
              <p className="text-sm text-slate-400 text-center py-2">Nog geen analyse beschikbaar</p>
            )}
          </div>
        </div>
      )}

      {/* Uitgebreide analyse — admin only, met historische context */}
      {isCompleted && user?.rol === 'admin' && (
        <div
          className="bg-white rounded-xl border border-slate-200 overflow-hidden"
          data-testid="uitgebreide-analyse-section"
        >
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <h3 className="font-semibold text-[#1f2548] text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4" /> Uitgebreide analyse
              <span className="text-xs font-normal text-slate-400">(admin)</span>
            </h3>
            <div className="flex gap-2">
              {!uitgebreid && !uitgebreidLoading && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fetchUitgebreid(false)}
                  data-testid="btn-uitgebreide-analyse"
                >
                  <BarChart3 className="w-3 h-3 mr-1" /> Analyse genereren
                </Button>
              )}
              {uitgebreid && !uitgebreidLoading && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fetchUitgebreid(true)}
                  data-testid="btn-uitgebreide-opnieuw"
                >
                  <Loader2 className="w-3 h-3 mr-1" /> Opnieuw genereren
                </Button>
              )}
            </div>
          </div>
          <div className="p-4">
            {uitgebreidLoading ? (
              <div className="flex items-center gap-2 text-slate-400 py-4 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Uitgebreide analyse wordt gegenereerd...
              </div>
            ) : uitgebreid?.analyse ? (
              <>
                <div
                  className="prose prose-sm max-w-none text-slate-700"
                  data-testid="uitgebreide-analyse-content"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(uitgebreid.analyse)) }}
                />
                <div className="mt-3 text-xs text-slate-500 border-t pt-2">
                  Gebaseerd op {uitgebreid.context?.aantal_historie ?? 0} eerdere invullingen
                  {uitgebreid.context?.performance_beschikbaar
                    ? ' en performance data van afgelopen week.'
                    : ' (geen performance data beschikbaar voor deze locatie).'}
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-400 text-center py-2">
                Klik op "Analyse genereren" voor een uitgebreide analyse met historische context en performance data.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      {!effectiveReadOnly && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 flex flex-col gap-2 z-30 sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:flex-row sm:items-center sm:gap-3"
          data-testid="fill-actions">
          <p className="text-xs text-slate-500 leading-tight text-center sm:text-left sm:mr-auto" data-testid="laatst-opgeslagen-indicator">
            {formatLastSaved(lastSaved)}
          </p>
          <div className="flex justify-between gap-3 sm:justify-end">
            <Button variant="outline" onClick={handleSaveDraft} disabled={saving} data-testid="save-draft-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Concept opslaan
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-[#1f2548] hover:bg-[#2a3060]" data-testid="submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Indienen
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
